/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios, { AxiosRequestConfig } from 'axios';
import urlJoin from 'proper-url-join';

import { ConvoSkill } from '../../../../ts/convo-skill';
import { endpoints } from '../../../ia/util/config';
import { TransactionUpdateResponse } from '../../store/convo-skills/actions';

/**
 * returns the endpoint for listing conversational skills
 */
const convoSkillEndpoint = urlJoin(
  endpoints.store,
  'v2/tooling/conversational_skills'
);

/**
 * Generates the correct endpoint for submitting /transactions posts
 * @param assistantId  the id of the parent assistant
 * @param actionSkillId  the id for the action skill within the assistant
 * @returns
 */
const transactionsEndpoint = (assistantId: string, actionSkillId: string) =>
  urlJoin(
    endpoints.store,
    'v2/tooling/assistants',
    assistantId,
    'skills',
    actionSkillId,
    'transactions'
  );

interface ListConvoSkillsParams {
  assistant_id: string;
  environment_id: string;
}

interface GetConvoSkillsParams {
  assistant_id: string;
  environment_id: string;
  provider_id: string;
}

interface ListConvoSkillResponseData {
  conversational_skills: ConvoSkill[];
}

export interface GetConvoSkillResponseData {
  data: ConvoSkill;
}

export async function listConvoSkills(
  params: ListConvoSkillsParams
): Promise<ListConvoSkillResponseData> {
  const config: AxiosRequestConfig = { params };
  const response = await axios.get(convoSkillEndpoint, config);
  return response.data;
}

export async function getConvoSkill(
  params: GetConvoSkillsParams,
  convSkillId: string
): Promise<GetConvoSkillResponseData> {
  const config: AxiosRequestConfig = { params };
  const response = await axios.get(
    `${convoSkillEndpoint}/${convSkillId}`,
    config
  );
  return response;
}

export async function createConvoSkill(
  assistantId: string,
  actionSkillId: string,
  createBody: any
): Promise<TransactionUpdateResponse> {
  const response = await axios.post(
    transactionsEndpoint(assistantId, actionSkillId),
    createBody
  );
  return response.data;
}

export async function updateConvoSkill(
  assistantId: string,
  actionSkillId: string,
  updateBody: any
): Promise<TransactionUpdateResponse> {
  const response = await axios.post(
    transactionsEndpoint(assistantId, actionSkillId),
    updateBody
  );
  return response.data;
}
